import { ArrowBack, SaveOutlined } from "@mui/icons-material";
import {
  Box,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserActions from "../../../../redux/users/users.actions";
import * as userReducer from "../../../../redux/users/users.reducers";
import Button3D from "../../../../utils/common/3d-button/Button3D";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import { primary } from "../../../../themes/theme";

const validationSchema = Yup.object().shape({
  mobile: Yup.string().trim().required("Mobile Number is required"),
  flat: Yup.string().trim().required("Flat Name is required"),
  landmark: Yup.string().trim().required("Landmark is required"),
  street: Yup.string().trim().required("Street Name is required"),
  city: Yup.string().trim().required("City is required"),
  state: Yup.string().trim().required("State is required"),
  country: Yup.string().trim().required("Country is required"),
  pinCode: Yup.string().trim().required("Pin Code is required"),
});

const EditShippingAddress: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { addressId } = useParams();

  //Get data from redux store
  const userState: userReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[userReducer.userFeatureKey];
    }
  );

  const { loading, address } = userState;

  useEffect(() => {
    if (addressId) {
      dispatch(UserActions.getAddressAction());
    }
  }, [addressId]);

  useEffect(() => {
    if (address) {
      // Other initial values assignment for formik initialValues
      formik.setValues({
        mobile: address?.mobile || "",
        flat: address?.flat || "",
        landmark: address?.landmark || "",
        street: address?.street || "",
        city: address?.city || "",
        state: address?.state || "",
        country: address?.country || "",
        pinCode: address?.pinCode || "",
      });
    }
  }, [address]);

  const handleUpdateAddress = async (values: any) => {
    dispatch(
      UserActions.updateAddressAction({
        address: values,
        addressId: address._id,
      })
    ).then((response: any) => {
      if (response && !response.error) {
        navigate("/users/profile");
      }
    });
  };

  const formik = useFormik({
    initialValues: {
      mobile: address?.mobile || "",
      flat: address?.flat || "",
      landmark: address?.landmark || "",
      street: address?.street || "",
      city: address?.city || "",
      state: address?.state || "",
      country: address?.country || "",
      pinCode: address?.pinCode || "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleUpdateAddress(values);
      console.log(values);
      // formik.resetForm();
    },
  });

  return (
    <>
      {loading && <SpinnerUI />}
      <Box sx={{ pb: 2, mb: 2 }}>
        <MainNavBar />
        <LayoutHeading
          heading="Edit Your Shipping Address"
          description="Efficiently update shipping address details for accurate and convenient deliveries."
        />
        <Grid
          container
          sx={{ gap: "10px", pl: { xs: 1, md: 9, lg: 10 }, pr: 1 }}
        >
          <Grid item xs={12}>
            <ArrowBack
              onClick={() => {
                navigate("/users/profile/");
              }}
              sx={{
                color: "grey",
                cursor: "pointer",
                "&:hover": { color: "black" },
              }}
            />
          </Grid>
          <Grid
            item
            xs={10}
            md={4}
            component={Paper}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "10px",
              height: "100%",
              position: "sticky",
              top: 90,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Ubuntu",
                  fontSize: "17px",
                  letterSpacing: "1px",
                  fontWeight: "550",
                }}
              >
                | Edit Shipping Address
              </Typography>
            </Box>
            <Divider
              sx={{
                mt: 1,
                mb: 2,
                color: primary[900],
                border: "1px dashed #f1f2f3",
              }}
            />
            <form onSubmit={formik.handleSubmit}>
              <TextField
                sx={{ my: 2 }}
                type="tel"
                placeholder="Mobile"
                variant="standard"
                {...formik.getFieldProps("mobile")}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                fullWidth
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="Flat"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("flat")}
                error={formik.touched.flat && Boolean(formik.errors.flat)}
                helperText={formik.touched.flat && formik.errors.flat}
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="Landmark"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("landmark")}
                error={
                  formik.touched.landmark && Boolean(formik.errors.landmark)
                }
                helperText={formik.touched.landmark && formik.errors.landmark}
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="Street"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("street")}
                error={formik.touched.street && Boolean(formik.errors.street)}
                helperText={formik.touched.street && formik.errors.street}
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="City"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("city")}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="State"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("state")}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="Country"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("country")}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
              />
              <TextField
                sx={{ my: 2 }}
                type="text"
                placeholder="Pin Code"
                variant="standard"
                fullWidth
                {...formik.getFieldProps("pinCode")}
                error={formik.touched.pinCode && Boolean(formik.errors.pinCode)}
                helperText={formik.touched.pinCode && formik.errors.pinCode}
              />
              <Box sx={{ py: 1 }}>
                <Button3D
                  type="submit"
                  buttonText="Update Address"
                  buttonIcon={
                    <SaveOutlined
                      fontSize="small"
                      sx={{ pr: "5px", pt: "2px", mt: "2px" }}
                    />
                  }
                />
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default EditShippingAddress;
